
/*!
 * Copyright (C) 2022-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-08-16
 *			- Refactor filter-widget.js hotel room call-to-action related functionality to <hotel-room-call-to-action> component.
 *
 *		2023-02-16 (jhoward)
 *			- Add resort fee mention to <hotel-room-call-to-action>.
 *			- Hide info dialog for resort fee info in <hotel-room-call-to-action>.
 *
 *		2023-03-15 (jhoward)
 *			- Add subtotal amount under price in to <hotel-room-call-to-action>.
 *
 *		2023-03-20 (jhoward)
 *			- Add boolean showRateDetails prop to <hotel-room-call-to-action>.
 *			- Add <hotel-room-call-to-action>::formatAsDollarAmount().
 *			- Round standard/preferred average rates in <hotel-room-call-to-action>::formatAsDollarAmount().
 *
 *		2024-06-10 (jhoward)
 *			- Implement Typescript in <hotel-room-call-to-action>.
 *			- Add showRateDetailTotals prop to <hotel-room-call-to-action> to facilitate displaying totals rather than daily averages.
 *			- Add addResortFeeToRoomRate prop to <hotel-room-call-to-action>.
 *      - Add rate detail dialog to <hotel-room-call-to-action>.
 *
 *		2024-06-19 (jhoward)
 *			- Fix adjacent night selection links in <hotel-room-call-to-action>.
*/

import {mapGetters, mapState} from "vuex";
import {defineComponent, PropType} from "vue";
import {HotelRoomAdjacentDate, HotelRoomAdjacentDateRooms} from "../../store/hotel-rooms";
import {HotelPage, RoomAvailabilityData} from "./defs";

export default defineComponent({
  name: 'hotelRoomCallToAction',
  props: {
    offerCodes: {
      type: Array as PropType<string[]>,
      required: true
    },
    page: {
      type: Object as PropType<HotelPage>,
      required: true
    },
    showLearnMoreButton: {
      type: Boolean,
      default: false
    },
    showRateDetails: {
      type: Boolean,
      default: false
    },
    showRateDetailTotals: {
      type: Boolean,
      default: false
    },
    addResortFeeToRoomRate: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      preferredLoggedIn: false,
      taxRate: 0.13,
      tourismSurchargeRate: 2,
    };
  },
  mounted() {
    $(() => {
      if (window.hasOwnProperty('peppermillPreferred') && window.peppermillPreferred.checkForPeppermillPreferredLoginToken()) {
        //user is logged in, let's check their token
        const tokenData = window.peppermillPreferred.getPeppermillPreferredLoginData();
        if (false !== tokenData) {
          this.preferredLoggedIn = true;
        }
      }
    });
  },
  computed: {
    ...mapState([
      'ROOMS',
      'ADJACENT_DATES',
      'isRoomAvailabilityLoaded',
      'checkInDate',
      'checkOutDate'
    ]),
    ...mapGetters([
      'stayLength',
      'bookingLink'
    ]),

    averagePriceStandardToDisplay() {
      return (this.addResortFeeToRoomRate)
        ? this.averagePriceStandardWithResortFee
        : this.averagePriceStandard;
    },
    averagePricePreferredToDisplay() {
      return (this.addResortFeeToRoomRate)
        ? this.averagePricePreferredWithResortFee
        : this.averagePricePreferred;
    },

    averagePriceStandardWithResortFee() {
      return this.averagePriceStandard + (this.roomAvailabilityData.ROOM_TYPES[0]?.RESORT_FEE_RATE || 0);
    },

    averagePricePreferredWithResortFee() {
      return this.averagePricePreferred + (this.roomAvailabilityData.ROOM_TYPES[0]?.RESORT_FEE_RATE || 0);
    },

    isAvailable(): boolean {
      return this.$store.getters.roomTypeIsAvailable(this.roomAvailabilityData);
    },
    isPreferredRate(): boolean {
      return this.averagePricePreferred > 0;
    },

    roomAvailabilityData(): RoomAvailabilityData {
      if (this.page.ID > 0) {
        for (let i = 0, l = this.$store.state.ROOMS.length; i < l; i++) {
          if (this.$store.state.ROOMS[i].PAGE_ID === this.page.ID) {
            return this.$store.state.ROOMS[i];
          }
        }
      }
      return {
        ADA_VARIANTS: [],
        DATA_AVAILABLE: false,
        PAGE_ID: 0,
        ROOM_TYPES: []
      };
    },

    minStayDays: function () {
      return this.$store.getters.roomTypeMinStayDays(this.roomAvailabilityData);
    },

    roomAdjacentDates: function () {
      return this.$store.state.ADJACENT_DATES.filter((adjacentDate: HotelRoomAdjacentDate) => {
        return adjacentDate.PAGE_ID === this.page.ID;
      });
    },

    averagePriceStandard: function () {
      if (Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].ROOM_AVERAGE_PRICE || 0;
      }

      return 0;
    },
    averagePricePreferred: function () {
      if (this.roomAvailabilityData.hasOwnProperty('ROOM_TYPES') && Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].PREFERRED_AVERAGE_PRICE || 0;
      }

      return 0;
    },

    totalPriceStandard: function () {
      if (this.roomAvailabilityData.hasOwnProperty('ROOM_TYPES') && Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].ROOM_TOTAL_PRICE || 0;
      }

      return 0;
    },
    totalPricePreferred: function () {
      if (this.roomAvailabilityData.hasOwnProperty('ROOM_TYPES') && Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].PREFERRED_TOTAL_PRICE || 0;
      }

      return 0;
    },

    preferredTooltipCopy: function () {
      return this.preferredLoggedIn
        ? 'You are logged in and will receive our member rate!'
        : 'Join our free Preferred program for member rates.';
    },
    preferredOverlayCopy: function () {
      return this.preferredLoggedIn
        ? 'You are logged in and will receive our member rate!'
        : 'Join our free Preferred program for member rates. Prices require Peppermill Preferred account (signup for free).';
    },

    averageRoomTax() {
      return this.averageSubtotal * this.taxRate;
    },
    totalRoomTax() {
      return this.subtotal * this.taxRate;
    },

    averageResortFee() {
      return (0 < this.roomAvailabilityData.ROOM_TYPES.length)
        ? this.roomAvailabilityData.ROOM_TYPES[0].RESORT_FEE_RATE
        : 0;
    },
    totalResortFee() {
      return this.averageResortFee * this.$store.getters.stayLength;
    },

    averageResortFeeTax() {
      return this.averageResortFee * this.taxRate;
    },
    totalResortFeeTax() {
      return this.totalResortFee * this.taxRate;
    },

    averageTourismSurcharge() {
      return this.tourismSurchargeRate;
    },
    totalTourismSurcharge() {
      return this.tourismSurchargeRate * this.$store.getters.stayLength;
    },

    averageSubtotal() {
      if (0 < this.totalPricePreferred) {
        return this.averagePricePreferred;
      } else if (0 < this.totalPriceStandard) {
        return this.averagePriceStandard;
      }

      return 0;
    },
    subtotal() {
      return (this.isPreferredRate)
        ? this.totalPricePreferred
        : this.totalPriceStandard;
    },

    averageTotal() {
      return this.averageSubtotal + this.averageRoomTax + this.averageResortFee + this.averageResortFeeTax + this.averageTourismSurcharge;
    },
    total() {
      return this.subtotal + this.totalRoomTax + this.totalResortFee + this.totalResortFeeTax + this.totalTourismSurcharge;
    },

    checkInDateFormatted(): string {
      if (this.$store.state.checkInDate) {
        return window.pepDateFunctions.formatShortDisplayDate(this.$store.state.checkInDate);
      }

      return '...';
    },
    checkOutDateFormatted(): string {
      if (this.$store.state.checkOutDate) {
        return window.pepDateFunctions.formatShortDisplayDate(this.$store.state.checkOutDate);
      }

      return '...';
    },

    bookNowLink(): string {
      return this.$store.getters.bookingLink(this.roomAvailabilityData, this.roomAvailabilityData.ROOM_TYPES[0]?.PRT_ID || 0, this.offerCodes[0] || '', this.page.ID);
    },
  },
  methods: {
    openRateDialog: function () {
      if (this.isPreferredRate) {
        this.openRateDialogPreferred();
      } else {
        this.openRateDialogStandard();
      }
    },
    openRateDialogPreferred: function () {
      window.showOverlayDialog({
        alertMode: true,
        confirmAccept: 'CLOSE',
        confirmTitle: 'Preferred Rate Information for Your Selected Stay',
        confirmBody: '<p>' + this.preferredOverlayCopy + '</p><p>$' + this.formatAsDollarAmount(this.averagePricePreferredToDisplay) + ' is the average nightly rate for the ' + this.page.TITLE + '. This includes resort fee and average room rate.</p>' + this.getRateTableHtml(),
        // callback: 'GENERIC-DIALOG-CLOSE'
      });
    },
    openRateDialogStandard: function () {
      window.showOverlayDialog({
        alertMode: true,
        confirmAccept: 'CLOSE',
        confirmTitle: 'Rate Information for Your Selected Stay',
        confirmBody: '<p>$' + this.formatAsDollarAmount(this.averagePriceStandardToDisplay) + ' is the average nightly rate for the ' + this.page.TITLE + '. This includes resort fee and average room rate.</p>' + this.getRateTableHtml(),
        // callback: 'GENERIC-DIALOG-CLOSE'
      });
    },

    getRateTableHtml(): string {
      return `<table class="hotel_room--cta_rate_table" aria-label="Rate information for selected stay">
          <tr>
            <td>Check In Date:</td>
            <td>${this.checkInDateFormatted}</td>
          </tr>
          <tr>
            <td>Check Out Date:</td>
            <td>${this.checkOutDateFormatted}</td>
          </tr>
          <tr>
            <td>Room Rate:</td>
            <td>$${this.formatAsDollarAmount(this.subtotal, false)}</td>
          </tr>
          <tr>
            <td>Room Tax:</td>
            <td>$${this.formatAsDollarAmount(this.totalRoomTax, false)}</td>
          </tr>
          <tr>
            <td>Resort Fee:</td>
            <td>$${this.formatAsDollarAmount(this.totalResortFee, false)}</td>
          </tr>
          <tr>
            <td>Resort Fee Tax:</td>
            <td>$${this.formatAsDollarAmount(this.totalResortFeeTax, false)}</td>
          </tr>
          <tr>
            <td>Tourism Surcharge:</td>
            <td>$${this.formatAsDollarAmount(this.totalTourismSurcharge, false)}</td>
          </tr>
          <tr>
            <td>Total:</td>
            <td>$${this.formatAsDollarAmount(this.total, false)}</td>
          </tr>
        </table>`;
    },

    getRatesClickHandler(event: MouseEvent) {
      if ('#' === this.bookNowLink) {
        event.preventDefault();
      }
    },

    updateCheckOutDateForMinNights() {
      if (this.minStayDays > 0 && !this.isAvailable && this.$store.state.checkInDate) {
        this.$store.commit('updateCheckOutDate', window.pepDateFunctions.addDays(this.$store.state.checkInDate, this.minStayDays));
        document.querySelector('.page-link-filter .pepvalidate-v3')?.dispatchEvent(new CustomEvent('submit', {detail: 'skipScroll'}));
      }
    },

    adjacentDateClickHandler(daysToShift: number): void {
      const saveCheckOutDate = new Date(this.$store.state.checkOutDate);
      this.$store.commit('updateCheckOutDate', null);
      this.$store.commit('updateCheckInDate', window.pepDateFunctions.addDays(this.$store.state.checkInDate, daysToShift));
      this.$store.commit('updateCheckOutDate', window.pepDateFunctions.addDays(saveCheckOutDate, daysToShift));

      document.querySelector('.page-link-filter .pepvalidate-v3')?.dispatchEvent(new CustomEvent('submit', {detail: 'skipScroll'}));
    },

    getAdjacentDatePrice(data: HotelRoomAdjacentDateRooms): string {
      if (data.hasOwnProperty(0)) {
        if (data[0].hasOwnProperty('PREFERRED_LOWEST_PRICE') && data[0].PREFERRED_LOWEST_PRICE) {
          return '$' + this.formatAsDollarAmount(data[0].PREFERRED_LOWEST_PRICE + this.averageResortFee);
        } else if (data[0].hasOwnProperty('ROOM_LOWEST_PRICE')) {
          return '$' + this.formatAsDollarAmount(data[0].ROOM_LOWEST_PRICE + this.averageResortFee);
        }
      }

      return '';
    },

    formatAsDollarAmount(amount: number, truncate = true): string {
      return '' + window.pepHelperFunctions.formatAsDollarAmount(amount, truncate);
    }
  }
})
